import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Input } from "@whoosh/design-system/src/components/controls/Input";
import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  getImageInput,
  getImageRenderingKey,
} from "@whoosh/utilities/src/Cloudinary";

import { EditPhoto } from "../../../../../features/ProfileUpdateForm/components";
import usePickImage from "../../../../../features/ProfileUpdateForm/components/EditPhoto/hooks/usePickImage";
import { UpdateMemberProfileInput } from "../../../../../features/ProfileUpdateForm/hooks/__generated__/useUpdateMemberProfileMutation.graphql";
import { useUpdateMemberProfile } from "../../../../../features/ProfileUpdateForm/hooks/useUpdateMemberProfile";
import { ClubProfileModalQuery } from "./__generated__/ClubProfileModalQuery.graphql";

type ClubProfileModalProps = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
};

export type EnabledTransportationTypes =
  | (
      | (
          | {
              id: string;
              code: string;
              default: boolean;
              enabled: boolean;
              memberVisible: boolean;
              customCode: string;
              memberGroups: any[];
            }
          | undefined
        )[]
      | undefined
    )[]
  | undefined;

export const ClubProfileModal = ({
  isOpen,
  setIsOpen,
}: ClubProfileModalProps) => {
  const [queryRef, loadProfileQuery] = useQueryLoader<ClubProfileModalQuery>(
    ClubProfileModalQueryString
  );

  useEffect(() => {
    loadProfileQuery({});
  }, [loadProfileQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <ClubProfileModalInner
          queryRef={queryRef}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      ) : null}
    </Suspense>
  );
};

const ClubProfileModalInner = ({
  isOpen,
  setIsOpen,
  queryRef,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  queryRef: PreloadedQuery<ClubProfileModalQuery>;
}) => {
  const data = usePreloadedQuery(ClubProfileModalQueryString, queryRef);
  const currentClientProfile = data.session?.currentClientProfile;

  const member = data.session?.currentClientProfile?.member;

  const {
    pickImage,
    removedImage,
    setRemovedImage,
    imageObject,
    setImageObject,
    isImageUploading,
  } = usePickImage(member?.club?.id);

  const hasProfileImage = removedImage
    ? false
    : getImageRenderingKey(imageObject) ||
      (member?.profileImage?.publicId && !isImageUploading);

  const { showToast } = useToast();

  const onUpdateCallback = () => {
    showToast({
      description: t`Profile updated successfully`,
      semanticStyle: "success",
      toastId: "update-profile-success",
    });

    setIsOpen(false);
  };

  const [updateMemberProfile, isUpdating] = useUpdateMemberProfile(() =>
    onUpdateCallback()
  );

  const handleSubmit = () => {
    const input: UpdateMemberProfileInput = {
      memberId: member?.id || "",
    };

    if (imageObject || removedImage) {
      input.profileImage = removedImage
        ? null
        : imageObject
        ? getImageInput(imageObject)
        : null;
    }

    updateMemberProfile(input);
  };

  const isSaveDisabled = isUpdating || isImageUploading;
  const isPublic = currentClientProfile?.member?.club?.supportsPublic;

  return (
    <WithRealSafeAreaInsets>
      <StandardModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        upperTitle={t`Club Profile`}
        hideFooterPadding
        subtitle={currentClientProfile?.member?.club?.name}
        content={
          <ScrollView style={{ flex: 1, paddingHorizontal: 24 }}>
            {isUpdating ? <Loader /> : null}
            <EditPhoto
              hasProfileImage={!!hasProfileImage}
              publicId={
                getImageRenderingKey(imageObject) ||
                member?.profileImage?.publicId
              }
              memberFirstName={member?.firstName}
              memberLastName={member?.lastName}
              isImageUploading={isImageUploading}
              pickImage={pickImage}
              setRemovedImage={setRemovedImage}
              setImageObject={setImageObject}
            />
            <VStack mt={8}>
              <Box>
                <Text variant="mainSmallMedium" mb={4}>
                  {isPublic ? t`Customer Type` : t`Membership Type`}
                </Text>
                <Input
                  isDisabled
                  value={member?.membershipType?.name ?? ""}
                  onChange={() => {}}
                  name="Membership Type"
                />
              </Box>
            </VStack>
          </ScrollView>
        }
        footerContent={
          <ScreenFooter>
            <Button
              isDisabled={isSaveDisabled}
              onPress={handleSubmit}
              accessibilityLabel={t`Save Changes`}
              testID="club-profile-modal-save-changes-button"
            >
              {t`Save Changes`}
            </Button>
          </ScreenFooter>
        }
      />
    </WithRealSafeAreaInsets>
  );
};

export const ClubProfileModalQueryString = graphql`
  query ClubProfileModalQuery {
    session {
      currentClientProfile {
        member {
          club {
            id
            name
            supportsPublic
          }
        }
        ... on MemberProfile {
          member {
            membershipType {
              name
            }
            club {
              id
              supportsPublic
            }
            ... on Member {
              id
              firstName
              lastName
              email
              phoneNumber {
                nationalNumber
              }
              profileImage {
                # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                publicId: renderingKey
              }
              memberGroups {
                id
              }
              departmentMemberProfiles(type: GOLF) {
                id
                department {
                  id
                  name
                  facilities {
                    edges {
                      node {
                        type
                        transportationTypes {
                          edges {
                            node {
                              id
                              code
                              name
                              enabled
                              memberVisible
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on Customer {
              id
              firstName
              lastName
              email
              phoneNumber {
                nationalNumber
              }
              profileImage {
                # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                publicId: renderingKey
              }
              departmentMemberProfiles(type: GOLF) {
                id
                department {
                  id
                  name
                  facilities {
                    edges {
                      node {
                        type
                        transportationTypes {
                          edges {
                            node {
                              id
                              code
                              enabled
                              name
                              memberVisible
                              customCode
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
